import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';
import { getNewsItem } from '../lib/api';
import { BackLink, EmptyState, LoadingScreen, SectionTag, TealButton } from '../components/common/BrandElements';

const NewsDetailPage = () => {
  const { slug } = useParams();
  const { t, isRTL } = useLanguage();
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const res = await getNewsItem(slug);
        setNewsItem(res.data);
      } catch (error) {
        console.error('Error loading news:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  if (loading) return <LoadingScreen />;

  if (!newsItem) {
    return (
      <div className="min-h-screen bg-raad-porcelain pt-40">
        <EmptyState
          title={t('الخبر غير موجود', 'News Not Found')}
          action={<TealButton to="/news">{t('العودة للأخبار', 'Back to News')}</TealButton>}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-raad-porcelain" data-testid="news-detail-page">
      <section className="relative overflow-hidden pt-36 pb-14 lg:pt-40">
        <div className="brand-grid absolute inset-0 opacity-55" />
        <div className="container relative z-10 mx-auto max-w-5xl px-6">
          <BackLink to="/news" label={t('العودة للأخبار', 'Back to News')} isRTL={isRTL} />
          <motion.header initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mt-10">
            <SectionTag>{t('خبر', 'News')}</SectionTag>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-raad-graphite/65 shadow-soft">
                <Calendar className="h-4 w-4 text-raad-teal" />
                {new Date(newsItem.published_at).toLocaleDateString(isRTL ? 'ar-SA' : 'en-US')}
              </span>
              {newsItem.tags?.map((tag, i) => (
                <span key={`${tag}-${i}`} className="rounded-full bg-raad-teal/10 px-3 py-1 text-xs font-black text-raad-teal">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="mt-5 text-4xl font-black leading-tight text-raad-charcoal sm:text-5xl lg:text-6xl text-balance">
              {t(newsItem.title_ar, newsItem.title_en)}
            </h1>
          </motion.header>
        </div>
      </section>

      <section className="pb-24">
        <div className="container mx-auto max-w-5xl px-6">
          <motion.article initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            {newsItem.image_url && (
              <div className="mb-8 overflow-hidden rounded-[2rem] border border-white bg-white shadow-soft">
                <img src={newsItem.image_url} alt={t(newsItem.title_ar, newsItem.title_en)} className="h-[520px] w-full object-cover" />
              </div>
            )}

            <div className="rounded-[2rem] border border-raad-teal/15 bg-white p-7 shadow-soft md:p-10">
              <p className="whitespace-pre-wrap text-lg leading-9 text-raad-graphite/75">
                {t(newsItem.content_ar, newsItem.content_en)}
              </p>
            </div>

            {newsItem.images?.length > 0 && (
              <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                {newsItem.images.map((image, index) => (
                  <div key={`${image}-${index}`} className="overflow-hidden rounded-[1.5rem] bg-white shadow-soft">
                    <img src={image} alt={`${t(newsItem.title_ar, newsItem.title_en)} - ${index + 1}`} className="h-72 w-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </motion.article>
        </div>
      </section>
    </div>
  );
};

export default NewsDetailPage;
